type StorageFreeAxiosLikeError = {
  config?: unknown;
  request?: unknown;
  response?: { status?: unknown; statusText?: unknown; data?: unknown };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Builds a one-line description of a failure that is safe to print to the
 * browser console or attach to telemetry:
 *
 * - no request payload (`error.config`) and no response body, because both can
 *   contain customer PII such as names, emails, phone numbers and addresses;
 * - no absolute URL query string, because it can carry ad click IDs;
 * - thrown strings and `error.message` are sanitized the same way — they can
 *   embed URLs, click-ID parameters (in a query string or as a bare
 *   `name=value` assignment) or emails verbatim;
 * - no first-party storage keys.
 */
export const describeErrorForLogs = (
  context: string,
  error: unknown,
): string => {
  const parts = [`[${context}]`];

  if (!isRecord(error)) {
    parts.push(
      typeof error === "string"
        ? sanitizeLogText(error)
        : "unknown failure",
    );
    return parts.join(" ");
  }

  if (typeof error.message === "string" && error.message !== "") {
    const sanitizedMessage = sanitizeLogText(error.message);

    if (sanitizedMessage !== "") {
      parts.push(sanitizedMessage);
    }
  }

  const candidate = error as StorageFreeAxiosLikeError;

  if (isRecord(candidate.response)) {
    if (typeof candidate.response.status === "number") {
      parts.push(`status=${candidate.response.status}`);
    }

    const config = isRecord(candidate.config) ? candidate.config : undefined;

    if (typeof config?.method === "string") {
      parts.push(`method=${config.method.toUpperCase()}`);
    }

    const requestUrl =
      typeof config?.url === "string" ? safeUrlPath(config.url) : null;

    if (requestUrl) {
      parts.push(`path=${requestUrl}`);
    }
  }

  return parts.join(" ");
};

const safeUrlPath = (rawUrl: string): string | null => {
  try {
    const parsedUrl = new URL(rawUrl, "https://reputech.invalid");

    return `${parsedUrl.pathname}`;
  } catch {
    return null;
  }
};

/** Bounding free text keeps one-line logs and limits what can slip through. */
const MAX_FREE_TEXT_LENGTH = 300;
const EMAIL_PATTERN =
  /[^\s<>()[\];:,"']+@[^\s<>()[\];:,"']+\.[^\s<>()[\];:,"']+/g;

// Single source of truth so both patterns below can never drift apart.
const CLICK_ID_PARAM_NAMES =
  "gclid|gbraid|wbraid|gclsrc|dclid|gad_source|gad_campaignid|fbclid|msclkid";
// Query-string form: consumes the leading `?`/`&` delimiter as well.
const CLICK_ID_PARAM_PATTERN = new RegExp(
  `[?&](?:${CLICK_ID_PARAM_NAMES})=[^\\s&]*`,
  "gi",
);
// Bare assignments in free text ("Google rejected gclid=EAIa…") have no
// delimiter before the name; a word boundary keeps `xgclid=` untouched.
const BARE_CLICK_ID_ASSIGNMENT_PATTERN = new RegExp(
  `\\b(?:${CLICK_ID_PARAM_NAMES})=[^\\s&,;]*`,
  "gi",
);

/**
 * Error messages and thrown strings are arbitrary attacker- and app-controlled
 * text: they routinely embed the failing URL (with click IDs in its query), a
 * bare `name=value` assignment, or the customer data that failed validation.
 * They get the same treatment as request URLs — paths stay, queries, emails
 * and identifiers go.
 */
export const sanitizeLogText = (value: string): string => {
  const withoutUrls = value.replace(/https?:\/\/\S+/gi, (match) =>
    safeUrlPath(match) ?? "",
  );

  const sanitized = withoutUrls
    .replace(CLICK_ID_PARAM_PATTERN, "")
    .replace(BARE_CLICK_ID_ASSIGNMENT_PATTERN, "")
    .replace(EMAIL_PATTERN, "[redacted-email]")
    .replace(/\s+/g, " ")
    .trim();

  return sanitized.length > MAX_FREE_TEXT_LENGTH
    ? `${sanitized.slice(0, MAX_FREE_TEXT_LENGTH)}...`
    : sanitized;
};

/**
 * Drop-in replacement for bare `console.log(e)` handlers around API calls.
 * Keeps failures diagnosable without exposing PII or click identifiers.
 */
export const logSafeError = (context: string, error: unknown): void => {
  console.error(describeErrorForLogs(context, error));
};
